// © 2026 AttainIP Engineering
import { Fragment, isValidElement, type ReactNode } from 'react';

export type DetailRow = [label: string, value: ReactNode | object | null | undefined];

/**
 * Two-column label/value grid used by expandable rows on the Audit Log,
 * Feedback, and Payments pages. Filters out null/undefined/empty values
 * so callers can list every conceivable field and rely on the grid to
 * suppress the irrelevant ones.
 *
 * Values may be:
 *   - scalar (string/number/boolean) — rendered as-is
 *   - object — JSON-stringified for display
 *   - React element — rendered directly (e.g., Link, anchor)
 */
export function DetailGrid({ rows }: { rows: DetailRow[] }) {
  const filtered = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (filtered.length === 0) return null;
  return (
    <div className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-1 text-xs font-mono">
      {filtered.map(([k, v]) => (
        <Fragment key={k}>
          <div className="text-gray-500">{k}</div>
          <div className="text-gray-800 break-all whitespace-pre-wrap">
            {isValidElement(v) || typeof v !== 'object'
              ? (v as ReactNode)
              : JSON.stringify(v, null, 2)}
          </div>
        </Fragment>
      ))}
    </div>
  );
}
